import { useCallback, useEffect, useState } from 'react';
import { useQueryClient } from '@tanstack/react-query';
import type { WorkQueueCase } from '../../../../services/workQueue';
import type { TaskBodyProps } from '../index';
import { invalidateCaseDetail } from '../../util';
import { ReviewSurface } from '../review-surface/ReviewSurface';
import { useApprovalReviewData } from './useApprovalReviewData';
import type { OrthopulseLevel } from './orthopulseLevel';
import { ApprovalActionsCard } from './ApprovalActionsCard';
import { DeclineCaseModal } from './DeclineCaseModal';
import { ExpireCaseModal } from './ExpireCaseModal';
import { ApproveCaseModal } from './ApproveCaseModal';
import { RequestChangesModal } from './RequestChangesModal';
import { approvalCompletion } from './approvalCompletion';

type ApprovalModal = 'decline' | 'approve' | 'expire' | 'request-changes' | null;

const DX_REPORT_LOAD_FAILED_REASON = 'The Dx report failed to load — retry before deciding.';

interface ApprovalModalsProps {
    modal: ApprovalModal;
    workCase: WorkQueueCase;
    orthopulseLevel: OrthopulseLevel | null;
    onClose: () => void;
    onSuccess: (actionId: string) => void;
}

function ApprovalModals({
    modal,
    workCase: c,
    orthopulseLevel,
    onClose,
    onSuccess
}: ApprovalModalsProps) {
    if (modal === 'decline') {
        return <DeclineCaseModal workCase={c} onClose={onClose} onSuccess={onSuccess} />;
    }
    if (modal === 'expire') {
        return <ExpireCaseModal workCase={c} onClose={onClose} onSuccess={onSuccess} />;
    }
    if (modal === 'request-changes') {
        return <RequestChangesModal workCase={c} onClose={onClose} onSuccess={onSuccess} />;
    }
    if (modal === 'approve') {
        return (
            <ApproveCaseModal
                workCase={c}
                orthopulseLevel={orthopulseLevel}
                onClose={onClose}
                onSuccess={onSuccess}
            />
        );
    }
    return null;
}

// Task-less on the engine (Init - 8's only outgoing edges are actor: 'external') - this is the frontend-only admin review UI.
export function ApprovalReviewTask({
    workCase: c,
    statusMeta,
    onPayloadChange,
    onTaskComplete
}: TaskBodyProps) {
    const queryClient = useQueryClient();
    const [modal, setModal] = useState<ApprovalModal>(null);

    // Fully custom surface — hide TaskSurface's auto-derived footer (there are
    // no internal-actor outgoing edges on this step to render anyway).
    useEffect(() => {
        onPayloadChange?.({ payload: null, valid: true, bodyOwnsSubmit: true });
        // eslint-disable-next-line react-hooks/exhaustive-deps
    }, []);

    const { versions, versionsState, hasMoreVersions, orthopulseLevel, refetch } =
        useApprovalReviewData(c.id);
    const actionsDisabled = versionsState === 'error';

    const handleSuccess = useCallback(
        (actionId: string) => {
            setModal(null);
            void invalidateCaseDetail(queryClient, c.id);
            onTaskComplete?.(approvalCompletion(statusMeta.outgoingActions, actionId));
        },
        [queryClient, onTaskComplete, c.id, statusMeta.outgoingActions]
    );

    return (
        <>
            <ReviewSurface
                patientId={c.id}
                versions={versions}
                docLabel="Dx report"
                versionsState={versionsState}
                onRetryVersions={refetch}
                hasMoreVersions={hasMoreVersions}
                actionsSlot={
                    <ApprovalActionsCard
                        onDecline={() => setModal('decline')}
                        onApprove={() => setModal('approve')}
                        onRequestChanges={() => setModal('request-changes')}
                        onExpire={() => setModal('expire')}
                        disabled={actionsDisabled}
                        disabledReason={actionsDisabled ? DX_REPORT_LOAD_FAILED_REASON : undefined}
                    />
                }
            />

            <ApprovalModals
                modal={modal}
                workCase={c}
                orthopulseLevel={orthopulseLevel}
                onClose={() => setModal(null)}
                onSuccess={handleSuccess}
            />
        </>
    );
}
